import * as pc from "playcanvas";
import type { SceneAssets } from "./scene";
import type { UnitDef } from "../../data/types";
import { KIND_UNIT, KIND_BUILDING, KIND_WRECKAGE } from "../../sim/constants";

// Per-(defId, kind) template + clone pool for every drawable sim entity, so a
// 200-unit brawl never thrashes the allocator: entities are acquired/released,
// kept in the scene graph and just toggled enabled.
//
// MESH-REGISTRY SEAM (for the future Higgsfield GLB drop-in): template() checks
// `unitTpl` FIRST and only falls back to the procedural SceneAssets factory. To
// swap primitives for a baked model per role, load the GLB and call
// registerUnitMesh(defId, entity) — no game/renderer code changes. Right now the
// registry is empty by directive (zero external assets → everything procedural).

export class ModelManager {
  readonly app: pc.AppBase;
  readonly scene: SceneAssets;
  readonly worldRoot: pc.Entity;
  ready = true;
  private unitTpl = new Map<string, pc.Entity>(); // defId -> GLB template (future)
  private otherTpl = new Map<string, pc.Entity>(); // key -> procedural template
  private pool = new Map<string, pc.Entity[]>(); // key -> free clones

  constructor(app: pc.AppBase, scene: SceneAssets, worldRoot: pc.Entity) {
    this.app = app;
    this.scene = scene;
    this.worldRoot = worldRoot;
  }

  /** No-op today (all-procedural). Kept as the GLB pipeline's future entry point. */
  async load(): Promise<boolean> {
    this.ready = true;
    return true;
  }

  /** Future GLB seam: register a per-def instantiated render entity as its mesh. */
  registerUnitMesh(defId: string, template: pc.Entity): void {
    this.unitTpl.set(defId, template);
  }

  private key(defId: string, kind: number): string {
    return kind + ":" + defId;
  }

  private template(def: UnitDef, kind: number): pc.Entity | null {
    if (kind === KIND_WRECKAGE) {
      const k = "W:" + def.id;
      let t = this.otherTpl.get(k);
      if (!t) { t = this.scene.buildWreck(def.radius); this.otherTpl.set(k, t); }
      return t;
    }
    // units, buildings and features: GLB registry first, else procedural.
    if (kind === KIND_UNIT || kind === KIND_BUILDING) {
      const glb = this.unitTpl.get(def.id);
      if (glb) return glb;
    }
    const k = "P:" + def.id;
    let t = this.otherTpl.get(k);
    if (!t) { t = this.scene.buildTemplate(def.role, def.faction, def.radius); this.otherTpl.set(k, t); }
    return t;
  }

  /** Get a pooled, enabled entity for (def, kind), added to the world graph. */
  acquire(def: UnitDef, kind: number): pc.Entity | null {
    const key = this.key(def.id, kind);
    const free = this.pool.get(key);
    if (free && free.length) { const e = free.pop()!; e.enabled = true; return e; }
    const tpl = this.template(def, kind);
    if (!tpl) return null;
    const e = tpl.clone() as pc.Entity;
    e.enabled = true;
    this.worldRoot.addChild(e);
    return e;
  }

  /** Return an entity to its pool (kept in the graph, disabled). */
  release(e: pc.Entity, defId: string, kind: number): void {
    e.enabled = false;
    const key = this.key(defId, kind);
    let free = this.pool.get(key);
    if (!free) { free = []; this.pool.set(key, free); }
    free.push(e);
  }
}
